import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, Alert, FlatList, Image} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  IconButton,
  Snackbar,
  Divider,
  FAB,
  Chip,
  TouchableRipple,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {colors, spacing} from '../../theme';

const ORG_TYPES = [
  {value: 'OUTLET', label: 'Outlet'},
  {value: 'FRANCHISE', label: 'Franchise'},
  {value: 'MASTER_FRANCHISE', label: 'Master Franchise'},
  {value: 'WAREHOUSE', label: 'Warehouse'},
  {value: 'OWNER_GROUP', label: 'Owner Group'},
];

const StoreListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {orgUnits, isLoading, fetchOrgUnits, deleteOrgUnit} =
    useOrgUnitStore();

  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchOrgUnits();
  }, [fetchOrgUnits]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      fetchOrgUnits();
    });
    return unsubscribe;
  }, [navigation, fetchOrgUnits]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchOrgUnits();
    setRefreshing(false);
  }, [fetchOrgUnits]);

  const handleDelete = useCallback(
    item => {
      Alert.alert(
        t('common.delete'),
        t('store.deleteConfirm', {name: item.name}),
        [
          {text: t('common.cancel'), style: 'cancel'},
          {
            text: t('common.delete'),
            style: 'destructive',
            onPress: async () => {
              try {
                await deleteOrgUnit(item.id);
              } catch (e) {
                setSnackbar(
                  e.response?.data?.error?.message || t('common.error'),
                );
              }
            },
          },
        ],
      );
    },
    [deleteOrgUnit, t],
  );

  const getOrgTypeLabel = useCallback(value => {
    const found = ORG_TYPES.find(ot => ot.value === value);
    return found ? found.label : value;
  }, []);

  const formatExpiryDate = useCallback(dateStr => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const isExpired = useCallback(item => {
    return item.subscriptionStatus === 'EXPIRED' ||
           item.subscriptionStatus === 'SUSPENDED';
  }, []);

  const getPlanStatusColor = useCallback(item => {
    if (!item.subscriptionStatus) return colors.textSecondary;
    switch (item.subscriptionStatus) {
      case 'ACTIVE':
        return colors.success;
      case 'TRIAL':
        return colors.info;
      case 'EXPIRED':
      case 'SUSPENDED':
        return colors.error;
      default:
        return colors.textSecondary;
    }
  }, []);

  const getPlanLabel = useCallback((item, tFn) => {
    if (!item.planName) return tFn('store.noPlan');
    if (item.subscriptionStatus === 'TRIAL') {
      return item.planName + ' (' + tFn('store.trial') + ')';
    }
    return item.planName;
  }, []);

  const renderItem = useCallback(
    ({item}) => {
      const planColor = getPlanStatusColor(item);
      const expired = isExpired(item);
      const locationParts = [item.area, item.city].filter(Boolean);
      const locationText = locationParts.join(', ');

      return (
        <TouchableRipple
          onPress={() =>
            navigation.navigate('StoreView', {storeId: item.id})
          }
          rippleColor={colors.primary + '20'}>
        <View style={styles.row}>
          {/* Store Image - ~20% */}
          <View style={styles.imageContainer}>
            {item.photoUrl || item.logoUrl ? (
              <Image
                source={{uri: item.photoUrl || item.logoUrl}}
                style={styles.storeImage}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.imagePlaceholder}>
                <Text style={styles.imagePlaceholderText}>
                  {item.name?.charAt(0)?.toUpperCase() || 'S'}
                </Text>
              </View>
            )}
          </View>

          {/* Info Block */}
          <View style={styles.infoBlock}>
            {/* Row 1: Name */}
            <Text style={styles.storeName} numberOfLines={1}>
              {item.name}
            </Text>

            {/* Row 2: Plan & Expiry */}
            <View style={styles.planRow}>
              <Text style={[styles.planText, {color: planColor}]}>
                {getPlanLabel(item, t)}
              </Text>
              {item.planExpiryDate ? (
                <Text
                  style={[
                    styles.expiryText,
                    expired && styles.expiredText,
                  ]}>
                  {expired
                    ? t('store.expired')
                    : t('store.expires') + ': ' + formatExpiryDate(item.planExpiryDate)}
                </Text>
              ) : null}
              {expired ? (
                <TouchableRipple
                  onPress={() => {
                    navigation.navigate('PlanSelection', {
                      orgUnitId: item.id,
                      orgUnitName: item.name,
                      subscriptionId: item.subscriptionId,
                    });
                  }}
                  style={styles.renewLink}>
                  <Text style={styles.renewText}>{t('store.renew')}</Text>
                </TouchableRipple>
              ) : null}
            </View>

            {/* Row 3: Org Type & Location */}
            <View style={styles.metaRow}>
              <Chip
                style={styles.typeChip}
                textStyle={styles.typeChipText}
                compact>
                <Text style={styles.typeChipLabel}>
                  {getOrgTypeLabel(item.orgType)}
                </Text>
              </Chip>
              {locationText ? (
                <View style={styles.locationRow}>
                  <Text style={styles.locationIcon}>{'📍'}</Text>
                  <Text style={styles.locationText} numberOfLines={1}>
                    {locationText}
                  </Text>
                </View>
              ) : null}
            </View>

            {/* Row 4: Actions */}
            <View style={styles.actionsRow}>
              <IconButton
                icon="pencil-outline"
                size={18}
                iconColor={colors.primary}
                onPress={() =>
                  navigation.navigate('StoreForm', {storeId: item.id})
                }
                style={styles.actionBtn}
              />
              <IconButton
                icon="delete-outline"
                size={18}
                iconColor={colors.error}
                onPress={() => handleDelete(item)}
                style={styles.actionBtn}
              />
            </View>
          </View>
        </View>
        </TouchableRipple>
      );
    },
    [
      navigation,
      handleDelete,
      getOrgTypeLabel,
      formatExpiryDate,
      isExpired,
      getPlanStatusColor,
      getPlanLabel,
      t,
    ],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  const ListHeader = useCallback(
    () => (
      <View>
        <View style={styles.descriptionRow}>
          <Text style={styles.descriptionText}>
            {t('store.description')}
          </Text>
        </View>
        <Divider />
      </View>
    ),
    [t],
  );

  const ListEmpty = useCallback(
    () => <Text style={styles.emptyText}>{t('common.noData')}</Text>,
    [t],
  );

  const ItemSeparator = useCallback(() => <Divider />, []);

  if (isLoading && orgUnits.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action
          icon="menu"
          onPress={() => navigation.dispatch(DrawerActions.openDrawer())}
        />
        <Appbar.Content
          title={t('store.title')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <FlatList
        data={orgUnits}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        ItemSeparatorComponent={ItemSeparator}
        refreshing={refreshing}
        onRefresh={handleRefresh}
      />

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={() => navigation.navigate('StoreForm')}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  descriptionText: {
    flex: 1,
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
  },

  // Row layout
  row: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    padding: spacing.sm,
    paddingHorizontal: spacing.md,
  },

  // Image (~20% of row width)
  imageContainer: {
    width: 72,
    height: 72,
    borderRadius: 10,
    overflow: 'hidden',
    marginRight: spacing.sm + 4,
    backgroundColor: colors.background,
  },
  storeImage: {
    width: '100%',
    height: '100%',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary + '15',
  },
  imagePlaceholderText: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.primary,
  },

  // Info block
  infoBlock: {
    flex: 1,
    justifyContent: 'center',
  },
  storeName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 2,
  },

  // Plan row
  planRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 4,
  },
  planText: {
    fontSize: 13,
    fontWeight: '600',
  },
  expiryText: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  expiredText: {
    color: colors.error,
    fontWeight: '600',
  },
  renewLink: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: colors.error + '12',
  },
  renewText: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.error,
  },

  // Meta row (org type + location)
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  typeChip: {
    backgroundColor: colors.textSecondary + '18',
    height: 24,
  },
  typeChipText: {
    fontSize: 11,
    color: colors.textSecondary,
  },
  typeChipLabel: {
    fontSize: 11,
    color: colors.textSecondary,
    marginTop: 0,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  locationIcon: {
    fontSize: 12,
    marginRight: 2,
  },
  locationText: {
    fontSize: 12,
    color: colors.textSecondary,
    flex: 1,
  },

  // Actions row
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 0,
    marginLeft: -8,
  },
  actionBtn: {
    margin: 0,
    width: 32,
    height: 32,
  },
});

export default StoreListScreen;
